import { prefersReducedMotion } from "@theme/utilities";

/**
 * Maximum translateY offset in pixels applied to image blocks at rest.
 * @constant {number}
 */
const OFFSET = 200;

/** @type {{ wrapper: HTMLElement | null, placeholder: HTMLElement | null, block: HTMLElement, observer: IntersectionObserver, onScroll: () => void }[]} */
const instances = [];

/**
 * Applies a scroll-driven parallax to image blocks inside checkerboard layouts.
 * Each image block is wrapped in a container div so the background behind the
 * sliding image can be controlled independently from the section background.
 *
 * Flush blocks are NOT wrapped — they stay as direct grid children so they can
 * visually overflow and overlap content above.
 */
function activate() {
	const checkerboards = document.querySelectorAll(".layout-checkerboard");
	if (!checkerboards.length) return;

	for (const grid of checkerboards) {
		const imageBlocks = /** @type {HTMLElement[]} */ (
			Array.from(grid.children).filter((child) =>
				child.querySelector(".group-block__media-wrapper"),
			)
		);

		for (const block of imageBlocks) {
			const flush = block.hasAttribute("data-start-flush");
			const scrollOffset = flush
				? Number(block.dataset.scrollOffset) || OFFSET
				: OFFSET;

			if (flush) {
				// No wrapper — block stays as a direct grid child so it can overflow.
				// Insert a placeholder behind it to fill the gap when it moves up.
				const placeholder = document.createElement("div");
				const computed = getComputedStyle(block);
				placeholder.style.gridColumn = computed.gridColumn;
				placeholder.style.gridRow = computed.gridRow;
				const placeholderBg = block.dataset.placeholderBg;
				if (placeholderBg) {
					placeholder.style.backgroundColor = placeholderBg;
				}
				block.parentNode.appendChild(placeholder);

				// Extend the image downward so it fills the gap when block moves up
				const imgContainer = block.querySelector(".background-image-container");
				if (imgContainer) {
					imgContainer.style.bottom = `-${scrollOffset}px`;
				}

				block.style.willChange = "transform";
				const { observer, onScroll } = observeElement(
					block,
					block,
					flush,
					scrollOffset,
				);
				instances.push({
					wrapper: null,
					placeholder,
					block,
					observer,
					onScroll,
				});
			} else {
				const wrapper = wrapElement(block);
				block.style.transform = `translateY(${OFFSET}px)`;
				block.style.willChange = "transform";
				const { observer, onScroll } = observeElement(
					wrapper,
					block,
					flush,
					scrollOffset,
				);
				instances.push({
					wrapper,
					placeholder: null,
					block,
					observer,
					onScroll,
				});
			}
		}
	}
}

/**
 * Removes all wrappers and resets transforms, restoring original DOM.
 */
function deactivate() {
	for (const { wrapper, placeholder, block, observer, onScroll } of instances) {
		observer.disconnect();
		window.removeEventListener("scroll", onScroll);

		// Unwrap: move block back to grid, remove wrapper
		if (wrapper) {
			const parent = wrapper.parentNode;
			if (parent) {
				parent.insertBefore(block, wrapper);
				parent.removeChild(wrapper);
			}
		}

		// Remove placeholder for flush blocks
		if (placeholder && placeholder.parentNode) {
			placeholder.parentNode.removeChild(placeholder);
		}

		// Reset inline styles
		block.style.transform = "";
		block.style.willChange = "";
		block.style.gridColumn = "";
		block.style.gridRow = "";
		block.style.height = "";
		block.style.width = "";
		const imgContainer = block.querySelector(".background-image-container");
		if (imgContainer) {
			imgContainer.style.bottom = "";
		}
	}

	instances.length = 0;
}

/**
 * Wraps an image block in a container div that inherits its grid position.
 * The wrapper stays fixed in the grid while the image slides inside it.
 * Only used for non-flush (default) blocks.
 *
 * @param {HTMLElement} el - The image block element to wrap.
 * @returns {HTMLElement} The wrapper element.
 */
function wrapElement(el) {
	const wrapper = document.createElement("div");
	wrapper.className = "checkerboard-scroll-wrapper";

	// Copy grid positioning from the element's computed style
	const computed = getComputedStyle(el);
	wrapper.style.gridColumn = computed.gridColumn;
	wrapper.style.gridRow = computed.gridRow;
	wrapper.style.overflow = "hidden";

	const placeholderBg = el.dataset.placeholderBg;
	if (placeholderBg) {
		wrapper.style.backgroundColor = placeholderBg;
	}

	el.parentNode.insertBefore(wrapper, el);
	wrapper.appendChild(el);

	// Reset the inner element's grid positioning since the wrapper handles it
	el.style.gridColumn = "";
	el.style.gridRow = "";
	el.style.height = "100%";
	el.style.width = "100%";

	return wrapper;
}

/**
 * Observes the target element and animates the image block's translateY
 * based on the target's position relative to the viewport.
 *
 * @param {HTMLElement} target - The element to observe (wrapper or block itself).
 * @param {HTMLElement} el - The image block element to animate.
 * @param {boolean} flush - If true, start at 0 and animate on exit.
 * @param {number} scrollOffset - How far (px) the image travels.
 * @returns {{ observer: IntersectionObserver, onScroll: () => void }}
 */
function observeElement(target, el, flush, scrollOffset) {
	/** @type {number | null} */
	let rafId = null;

	const update = () => {
		const rect = target.getBoundingClientRect();
		const viewportHeight = window.innerHeight;

		if (flush) {
			const raw = Math.min(
				Math.max((viewportHeight - rect.top) / viewportHeight, 0),
				1,
			);
			const progress = raw;
			el.style.transform = `translateY(${-scrollOffset * progress}px)`;
		} else {
			// Default: start offset, slide into place as section enters
			const progress = Math.min(
				Math.max((viewportHeight - rect.top) / viewportHeight, 0),
				1,
			);
			const y = Math.max(OFFSET * (1 - progress * 1.3), 0);
			el.style.transform = `translateY(${y}px)`;
		}
	};

	const onScroll = () => {
		if (rafId !== null) return;
		rafId = requestAnimationFrame(() => {
			rafId = null;
			update();
		});
	};

	const observer = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting) {
					window.addEventListener("scroll", onScroll, { passive: true });
					update();
				} else {
					window.removeEventListener("scroll", onScroll);
					if (rafId !== null) {
						cancelAnimationFrame(rafId);
						rafId = null;
					}
				}
			}
		},
		{ rootMargin: `${scrollOffset}px 0px 0px 0px`, threshold: 0 },
	);

	observer.observe(target);
	return { observer, onScroll };
}

if (!prefersReducedMotion()) {
	const mq = window.matchMedia("(min-width: 750px)");

	if (mq.matches) activate();

	mq.addEventListener("change", (e) => {
		if (e.matches) {
			activate();
		} else {
			deactivate();
		}
	});
}
